import { deepmerge } from '@mui/utils';
import { darken, alpha } from '@mui/material/styles';
import { fromTheme as kyruusFromTheme } from '@kyruus/ui-theme';

/**
 * Converts a pixel value string (e.g. "16px") to a number.
 * @param {string} key - The pixel value string to convert.
 * @returns {number} The numeric value of the pixel string.
 */
const convertToNumber = (key) => parseInt(key.replace('px', ''), 10);

/**
 * Generates MUI Button component style overrides using theme values.
 * @param {function} fromTheme - Function to retrieve theme values by key.
 * @returns {object} MUI Button style overrides for the theme.
 *
 * The returned object customizes the appearance of MUI Buttons, including
 * background color, border, font, and disabled/focus states, based on the
 * provided theme values.
 */
const buttonStyles = (fromTheme, biomeTheme) => {
  const buttonColor = fromTheme('color_button');
  const borderRadius = fromTheme('border_radius');
  const borderWidth = fromTheme('border_width');
  const fontFace = fromTheme('font_face');
  const fontSize = fromTheme('font_size');
  const fontWeight = fromTheme('font_weight');

  return {
    // style taken from biome and updated accordingly
    MuiButton: {
      styleOverrides: {
        root: ({ theme: currentTheme }) => ({
          ...biomeTheme.components.MuiButton.styleOverrides.root({
            theme: currentTheme
          }),
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          fontFamily: fontFace,
          fontSize: fontSize,
          fontWeight: fontWeight
        }),
        contained: ({ theme: currentTheme }) => ({
          ...biomeTheme.components.MuiButton.styleOverrides.contained({
            theme: currentTheme
          }),
          '&:not(:disabled, .disabled)': {
            color: currentTheme.palette.common.white,
            borderColor: buttonColor,
            backgroundColor: buttonColor,
            '&:hover, &:focus, &.hover, &.focus': {
              borderColor: darken(buttonColor, 0.2),
              backgroundColor: darken(buttonColor, 0.2)
            },
            '&:focus:not(:focus-visible):not(:hover)': {
              boxShadow: 'none',
              borderColor: buttonColor,
              backgroundColor: buttonColor
            },
            '&:focus-visible, &.focus': {
              borderColor: currentTheme.palette.common.white,
              boxShadow: `0 0 0 1px ${
                currentTheme.palette.common.white
              }, 0 0 0 3px ${darken(buttonColor, 0.2)}`
            },
            '&:active, &.active': {
              boxShadow: 'none',
              backgroundColor: darken(buttonColor, 0.4),
              borderColor: darken(buttonColor, 0.4)
            }
          }
        }),
        outlinedPrimary: ({ theme: currentTheme }) => ({
          ...biomeTheme.components.MuiButton.styleOverrides.outlinedPrimary({
            theme: currentTheme
          }),
          borderColor: buttonColor
        }),
        outlined: ({ theme: currentTheme }) => ({
          '&.MuiButton-outlinedNeutral, &.MuiButton-outlinedDefault': {
            borderColor: currentTheme.palette.grey[600],
            '&:hover, &.hover': {
              backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
            },
            '&:focus:not(:focus-visible)': {
              outline: 'none',
              boxShadow: 'none'
            },
            '&:focus-visible, &.focus': {
              borderColor: currentTheme.palette.grey[900],
              boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
            },
            '&:active, &.active': {
              boxShadow: 'none',
              backgroundColor: alpha(currentTheme.palette.grey[500], 0.2),
              borderColor: currentTheme.palette.grey[900]
            },
            '&:not(:disabled, .disabled)': {
              color: currentTheme.palette.common.black
            },
            '&:disabled, &.disabled': {
              boxShadow: 'none',
              borderColor: currentTheme.palette.grey[200],
              backgroundColor: 'transparent'
            }
          },
          '&:disabled, &.disabled': {
            backgroundColor: 'transparent',
            borderColor: currentTheme.palette.grey[200],
            boxShadow: 'none'
          }
        })
      }
    }
  };
};

/**
 * Builds a custom MUI theme by merging Kyruus and Biome theme objects.
 * Applies palette, typography, breakpoints, shape, and component overrides.
 * @param {object} kyruusTheme - The Kyruus theme object
 * @param {object} biomeTheme - The Biome theme object (which is a MUI theme)
 * @returns {object} The merged MUI theme object, ready for use with ThemeProvider.
 *
 * This function extracts color, typography, and layout values from the Kyruus theme,
 * constructs a MUI-compatible theme structure, and merges it with the Biome theme.
 * It also applies custom component overrides, such as for MUI Button, to ensure
 * consistent styling across the application.
 */
export const buildKyruusBiomeTheme = (kyruusTheme, biomeTheme) => {
  // wrap fromTheme to simplify the code below
  const fromTheme = (themeKey) =>
    kyruusFromTheme(themeKey)({ theme: kyruusTheme });

  const colorProvidermatch = fromTheme('color_providermatch');
  const colorSuccess = fromTheme('color_success');
  const colorInfo = fromTheme('color_info');
  const colorWarning = fromTheme('color_warning');
  const colorDanger = fromTheme('color_danger');

  const colorProvidermatchSecondary = fromTheme(
    'color_providermatch_secondary'
  );
  const colorSuccessSecondary = fromTheme('color_success_secondary');
  const colorInfoSecondary = fromTheme('color_info_secondary');
  const colorWarningSecondary = fromTheme('color_warning_secondary');
  const colorDangerSecondary = fromTheme('color_danger_secondary');

  const fontFace = fromTheme('font_face');
  const fontSize = convertToNumber(fromTheme('font_size'));
  const fontWeightLight = fromTheme('font_weight_light');
  const fontWeight = fromTheme('font_weight');
  const fontWeightBold = fromTheme('font_weight_bold');

  const borderRadius = fromTheme('border_radius');

  return deepmerge(biomeTheme, {
    // primitives (base styles) that are applied within biome components (taken from createMuiThemeFromEmotionTheme in kyruus-components)
    palette: {
      primary: {
        main: colorProvidermatch,
        50: colorProvidermatchSecondary,
        100: colorProvidermatchSecondary,
        200: colorProvidermatch,
        300: colorProvidermatch,
        400: colorProvidermatch,
        500: colorProvidermatch,
        600: colorProvidermatch,
        700: colorProvidermatch,
        800: colorProvidermatch,
        900: colorProvidermatch,
        A100: colorProvidermatchSecondary,
        A200: colorProvidermatch,
        A400: colorProvidermatch,
        A700: colorProvidermatch,
        contrastDefaultColor: 'light'
      },
      secondary: {
        main: colorProvidermatchSecondary,
        50: colorProvidermatchSecondary,
        100: colorProvidermatchSecondary,
        200: colorProvidermatch,
        300: colorProvidermatch,
        400: colorProvidermatch,
        500: colorProvidermatch,
        600: colorProvidermatch,
        700: colorProvidermatch,
        800: colorProvidermatch,
        900: colorProvidermatch,
        A100: colorProvidermatchSecondary,
        A200: colorProvidermatch,
        A400: colorProvidermatch,
        A700: colorProvidermatch,
        contrastDefaultColor: 'light'
      },
      success: {
        main: colorSuccess,
        50: colorSuccessSecondary,
        100: colorSuccessSecondary,
        200: colorSuccess,
        300: colorSuccess,
        400: colorSuccess,
        500: colorSuccess,
        600: colorSuccess,
        700: colorSuccess,
        800: colorSuccess,
        900: colorSuccess,
        A100: colorSuccessSecondary,
        A200: colorSuccess,
        A400: colorSuccess,
        A700: colorSuccess,
        contrastDefaultColor: 'light'
      },
      info: {
        main: colorInfo,
        50: colorInfoSecondary,
        100: colorInfoSecondary,
        200: colorInfo,
        300: colorInfo,
        400: colorInfo,
        500: colorInfo,
        600: colorInfo,
        700: colorInfo,
        800: colorInfo,
        900: colorInfo,
        A100: colorInfoSecondary,
        A200: colorInfo,
        A400: colorInfo,
        A700: colorInfo,
        contrastDefaultColor: 'light'
      },
      warning: {
        main: colorWarning,
        50: colorWarningSecondary,
        100: colorWarningSecondary,
        200: colorWarning,
        300: colorWarning,
        400: colorWarning,
        500: colorWarning,
        600: colorWarning,
        700: colorWarning,
        800: colorWarning,
        900: colorWarning,
        A100: colorWarningSecondary,
        A200: colorWarning,
        A400: colorWarning,
        A700: colorWarning,
        contrastDefaultColor: 'light'
      },
      error: {
        main: colorDanger,
        50: colorDangerSecondary,
        100: colorDangerSecondary,
        200: colorDanger,
        300: colorDanger,
        400: colorDanger,
        500: colorDanger,
        600: colorDanger,
        700: colorDanger,
        800: colorDanger,
        900: colorDanger,
        A100: colorDangerSecondary,
        A200: colorDanger,
        A400: colorDanger,
        A700: colorDanger,
        contrastDefaultColor: 'light'
      },
      typography: {
        fontFamily: fontFace,
        fontSize: fontSize,
        caption: {
          fontSize: fontSize
        },
        fontWeightLight: fontWeightLight,
        fontWeightRegular: fontWeight,
        fontWeightMedium: fontWeightBold,
        button: {
          fontSize: fontSize,
          fontWeight: fontWeightBold,
          fontFamily: fontFace,
          textTransform: 'none'
        }
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: convertToNumber(fromTheme('screen_small')),
          md: convertToNumber(fromTheme('screen_medium')),
          lg: convertToNumber(fromTheme('screen_large')),
          xl: convertToNumber(fromTheme('screen_large'))
        },
        unit: 'px',
        step: 5
      },
      shape: {
        borderRadius: borderRadius
      }
    },

    // individual component overrides should go into their own functions to keep this manageable/easy to read
    components: {
      ...buttonStyles(fromTheme, biomeTheme)
    }
  });
};
